import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCheck,
  faRotate,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { getUser, postUser } from "../../helper/http";
import styles from "./DeliveryAccessPage.module.css";

type DeliveryRole = "admin" | "manager" | "logistician";

interface AccessRequest {
  id: number;
  hsh: string;
  display_name?: string;
  messenger_user_id?: string;
  requested_role: DeliveryRole;
  role?: DeliveryRole;
  status: string;
  requested_at: string;
  approved_at?: string;
  updated_at: string;
}

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleString() : "-";
}

export default function DeliveryAccessPage() {
  const [rows, setRows] = useState<AccessRequest[]>([]);
  const [roles, setRoles] = useState<Record<number, DeliveryRole>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getUser("api/delivery/admin/access");
      const nextRows = response?.response || [];
      setRows(nextRows);
      setRoles(
        Object.fromEntries(
          nextRows.map((row: AccessRequest) => [
            row.id,
            row.role || row.requested_role || "manager",
          ]),
        ),
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load access requests",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (
    id: number,
    decision: "approve" | "reject" | "revoke",
  ) => {
    setBusyId(id);
    setError("");
    try {
      await postUser("api/delivery/admin/access/decision", {
        id,
        decision,
        role: roles[id] || "manager",
      });
      await load();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Access decision failed",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Delivery Access</h1>
          <span>
            {rows.filter((row) => row.status === "pending").length} pending
            requests
          </span>
        </div>
        <button type="button" onClick={load} disabled={loading} title="Refresh">
          <FontAwesomeIcon icon={faRotate} />
          <span>{loading ? "Loading" : "Refresh"}</span>
        </button>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Messenger ID</th>
              <th>Requested</th>
              <th>Granted role</th>
              <th>Status</th>
              <th>Updated</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.display_name || `Request #${row.id}`}</strong>
                  <small className={styles.hash}>{row.hsh}</small>
                </td>
                <td>{row.messenger_user_id || "-"}</td>
                <td>
                  <span>{row.requested_role}</span>
                  <small>{formatDate(row.requested_at)}</small>
                </td>
                <td>
                  <select
                    value={roles[row.id] || "manager"}
                    onChange={(event) =>
                      setRoles((current) => ({
                        ...current,
                        [row.id]: event.target.value as DeliveryRole,
                      }))
                    }
                    disabled={busyId === row.id}
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="logistician">Logistician</option>
                  </select>
                </td>
                <td>
                  <span
                    className={`${styles.badge} ${styles[row.status] || ""}`}
                  >
                    {row.status}
                  </span>
                </td>
                <td>{formatDate(row.updated_at)}</td>
                <td>
                  <div className={styles.actions}>
                    {row.status !== "approved" && (
                      <button
                        type="button"
                        className={styles.approve}
                        onClick={() => decide(row.id, "approve")}
                        disabled={busyId === row.id}
                        title="Approve"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    )}
                    {row.status === "pending" && (
                      <button
                        type="button"
                        className={styles.reject}
                        onClick={() => decide(row.id, "reject")}
                        disabled={busyId === row.id}
                        title="Reject"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                    {row.status === "approved" && (
                      <button
                        type="button"
                        className={styles.revoke}
                        onClick={() => decide(row.id, "revoke")}
                        disabled={busyId === row.id}
                        title="Revoke"
                      >
                        <FontAwesomeIcon icon={faBan} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  No access requests yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}
